import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPInput from '../../../../../components/IPInput'
import IPCheckBox from '../../../../../components/IPCheckBox'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import { CompayPaymentSettings, CompayPaymentCategorySettings } from '../../models/models'
import { GetSettingsApi, UpdateSettingsApi, GetCategorySettingsApi, UpdateCategorySettingsApi } from '../../services/compayPaymentApi'
import './style.scss'

interface ShoppingCreditSettingsState {
  settings: CompayPaymentSettings | null
  form: CompayPaymentSettings
  isLoading: boolean
  isSaving: boolean
  creditUserTypeOptions: ISelectOption[]
  categorySettings: CompayPaymentCategorySettings[]
  categoryForm: CompayPaymentCategorySettings[]
  isLoadingCategories: boolean
  isSavingCategories: boolean
}

function ShoppingCreditSettings(props: BaseComponentWithContextProps) {
  const creditUserTypeOptions: ISelectOption[] = [
    { value: 'DEALER', label: 'Satıcı Kredisi' },
    { value: 'CUSTOMER', label: 'Müşteri Kredisi' },
  ]

  const getInitialState = (): ShoppingCreditSettingsState => ({
    settings: null,
    form: {
      MaxDealerMarkupPercentage: 0,
      MinDealerMarkupPercentage: 0,
      IsCustomerCreditEnabled: false,
      IsDealerCreditEnabled: false,
      RequireCustomerTcNo: false,
      RequireCustomerName: false,
      SapProfitTransferEnabled: false,
      DefaultCreditUserType: 'DEALER',
    },
    isLoading: false,
    isSaving: false,
    creditUserTypeOptions,
    categorySettings: [],
    categoryForm: [],
    isLoadingCategories: false,
    isSavingCategories: false,
  })

  const [state, setState] = useCustomReducer<ShoppingCreditSettingsState>(getInitialState())
  const [selectedDefaultCreditUserType, setSelectedDefaultCreditUserType] = useState<ISelectOption | null>(null)

  // Fetch settings on component mount
  useEffect(() => {
    loadSettings()
    loadCategorySettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update selected credit user type when form changes
  useEffect(() => {
    const selected = creditUserTypeOptions.find(opt => opt.value === state.form.DefaultCreditUserType)
    setSelectedDefaultCreditUserType(selected || null)
  }, [state.form.DefaultCreditUserType])

  const loadSettings = async () => {
    try {
      setState({ isLoading: true })
      const settingsApi = new GetSettingsApi({})
      const settings = await settingsApi.getSettings()

      setState({
        settings,
        form: {
          MaxDealerMarkupPercentage: settings.MaxDealerMarkupPercentage || 0,
          MinDealerMarkupPercentage: settings.MinDealerMarkupPercentage || 0,
          IsCustomerCreditEnabled: settings.IsCustomerCreditEnabled || false,
          IsDealerCreditEnabled: settings.IsDealerCreditEnabled || false,
          RequireCustomerTcNo: settings.RequireCustomerTcNo || false,
          RequireCustomerName: settings.RequireCustomerName || false,
          SapProfitTransferEnabled: settings.SapProfitTransferEnabled || false,
          DefaultCreditUserType: settings.DefaultCreditUserType || 'DEALER',
        },
        isLoading: false,
      })
    } catch (err: any) {
      setState({ isLoading: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Ayarlar yüklenirken bir hata oluştu.',
      })
    }
  }

  const loadCategorySettings = async () => {
    try {
      setState({ isLoadingCategories: true })
      const categoryApi = new GetCategorySettingsApi({})
      const categorySettings = await categoryApi.getCategorySettings()

      setState({
        categorySettings,
        categoryForm: categorySettings,
        isLoadingCategories: false,
      })
    } catch (err: any) {
      setState({ isLoadingCategories: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Kategori ayarları yüklenirken bir hata oluştu.',
      })
    }
  }

  const handleInputChange = (fieldName: keyof CompayPaymentSettings, value: any) => {
    setState({
      form: {
        ...state.form,
        [fieldName]: value,
      },
    })
  }

  const handleCheckboxChange = (fieldName: keyof CompayPaymentSettings, checked: boolean) => {
    setState({
      form: {
        ...state.form,
        [fieldName]: checked,
      },
    })
  }

  const handleDefaultCreditUserTypeChange = (selected: ISelectOption) => {
    if (selected) {
      setState({
        form: {
          ...state.form,
          DefaultCreditUserType: selected.value as string,
        },
      })
      setSelectedDefaultCreditUserType(selected)
    }
  }

  const handleCategorySettingChange = (index: number, fieldName: keyof CompayPaymentCategorySettings, value: any) => {
    const updatedCategories = [...state.categoryForm]
    updatedCategories[index] = {
      ...updatedCategories[index],
      [fieldName]: value,
    }
    setState({ categoryForm: updatedCategories })
  }

  const handleCategorySave = async () => {
    try {
      setState({ isSavingCategories: true })

      // Sadece değişiklik yapılmış kategorileri filtrele
      const categoriesToSend = state.categoryForm
        .map(category => ({
          ...category,
          CategoryId: category.Id > 0 ? category.Id : 0,
        }))
        .filter(changedCategory => {
          // Yeni kategori ise (ID <= 0) gönder
          if (changedCategory.Id <= 0) return true

          // Orijinal kategoride değişiklik olup olmadığını kontrol et
          const originalCategory = state.categorySettings.find(c => c.Id === changedCategory.Id)
          if (!originalCategory) return true // Eğer orijinalde yoksa gönder

          // Değişen alanları kontrol et
          const hasChanged =
            originalCategory.CategoryName !== changedCategory.CategoryName ||
            originalCategory.IsActive !== changedCategory.IsActive ||
            originalCategory.MinPrice !== changedCategory.MinPrice ||
            originalCategory.MaxPrice !== changedCategory.MaxPrice

          return hasChanged
        })

      const updateCategoryApi = new UpdateCategorySettingsApi({})
      const updatedCategories = await updateCategoryApi.updateCategorySettings(categoriesToSend as any)

      // API'den gelen güncel kategori listesi ile state'i güncelle
      setState({
        categorySettings: updatedCategories,
        categoryForm: updatedCategories,
        isSavingCategories: false,
      })

      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Kategori ayarları başarıyla kaydedildi.',
      })
    } catch (err: any) {
      setState({ isSavingCategories: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Kategori ayarları kaydedilirken bir hata oluştu.',
      })
    }
  }

  const handleCategoryReset = () => {
    setState({
      categoryForm: [...state.categorySettings],
    })
  }

  const handleAddCategory = () => {
    const newCategory: CompayPaymentCategorySettings = {
      Id: Math.min(...state.categoryForm.map(c => c.Id), 0), // Negatif ID för új kategóriák
      CategoryName: '',
      IsActive: true,
      MinPrice: undefined,
      MaxPrice: undefined,
    }
    setState({
      categoryForm: [...state.categoryForm, newCategory],
    })
  }

  const handleDeleteCategory = (index: number) => {
    const updatedCategories = state.categoryForm.filter((_, i) => i !== index)
    setState({
      categoryForm: updatedCategories,
    })
  }

  const handleReset = () => {
    if (state.settings) {
      setState({
        form: {
          MaxDealerMarkupPercentage: state.settings.MaxDealerMarkupPercentage || 0,
          MinDealerMarkupPercentage: state.settings.MinDealerMarkupPercentage || 0,
          IsCustomerCreditEnabled: state.settings.IsCustomerCreditEnabled || false,
          IsDealerCreditEnabled: state.settings.IsDealerCreditEnabled || false,
          RequireCustomerTcNo: state.settings.RequireCustomerTcNo || false,
          RequireCustomerName: state.settings.RequireCustomerName || false,
          SapProfitTransferEnabled: state.settings.SapProfitTransferEnabled || false,
          DefaultCreditUserType: state.settings.DefaultCreditUserType || 'DEALER',
        },
      })
    }
  }

  const handleSave = async () => {
    try {
      setState({ isSaving: true })
      const updateSettingsApi = new UpdateSettingsApi({})
      await updateSettingsApi.updateSettings(state.form)

      setState({
        settings: state.form,
        isSaving: false,
      })

      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Ayarlar başarıyla kaydedildi.',
      })
    } catch (err: any) {
      setState({ isSaving: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Ayarlar kaydedilirken bir hata oluştu.',
      })
    }
  }

  if (state.isLoading) {
    return <div className="shopping-credit-settings loading">Yükleniyor...</div>
  }

  return (
    <div className="shopping-credit-settings">
      <div className="settings-header">
        <h2>Alışveriş Kredisi Ayarları</h2>
      </div>

      <form className="settings-form">
        <div className="form-sections-grid">
          <div className="form-section">
            <h3>Para Birimi & Markup Ayarları</h3>

            <div className="form-group">
              <label>Maksimum Satıcı Markup Yüzdesi (%)</label>
              <IPInput
                type="number"
                value={state.form.MaxDealerMarkupPercentage?.toString() || ''}
                onChange={(e: any) => handleInputChange('MaxDealerMarkupPercentage', parseFloat(e.target.value) || 0)}
                placeholder="Maksimum markup yüzdesini girin"
              />
            </div>

            <div className="form-group">
              <label>Minimum Satıcı Markup Yüzdesi (%)</label>
              <IPInput
                type="number"
                value={state.form.MinDealerMarkupPercentage?.toString() || ''}
                onChange={(e: any) => handleInputChange('MinDealerMarkupPercentage', parseFloat(e.target.value) || 0)}
                placeholder="Minimum markup yüzdesini girin"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>SAP & Varsayılan Ayarlar</h3>

            <div className="form-group checkbox-group">
              <IPCheckBox
                label="SAP Kâr Transferi Etkin"
                checked={state.form.SapProfitTransferEnabled || false}
                onCheckedChange={(checked: boolean) => handleCheckboxChange('SapProfitTransferEnabled', checked)}
              />
            </div>

            <div className="form-group">
              <label>Varsayılan Kredi Kullanıcı Türü</label>
              <IPSelectBox
                options={creditUserTypeOptions}
                value={selectedDefaultCreditUserType}
                onChangeSingle={handleDefaultCreditUserTypeChange}
                placeholder="Kredi kullanıcı türü seçin"
              />
            </div>
          </div>
          <div className="form-section">
            <h3>Kredi Türü Aktivasyonu</h3>

            <div className="form-group checkbox-group">
              <IPCheckBox
                label="Müşteri Kredisi Etkin"
                checked={state.form.IsCustomerCreditEnabled || false}
                onCheckedChange={(checked: boolean) => handleCheckboxChange('IsCustomerCreditEnabled', checked)}
              />
            </div>

            <div className="form-group checkbox-group">
              <IPCheckBox
                label="Satıcı Kredisi Etkin"
                checked={state.form.IsDealerCreditEnabled || false}
                onCheckedChange={(checked: boolean) => handleCheckboxChange('IsDealerCreditEnabled', checked)}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Müşteri Bilgi Gereklilikleri</h3>

            <div className="form-group checkbox-group">
              <IPCheckBox
                label="Müşteri TC Numarası Gerekli"
                checked={state.form.RequireCustomerTcNo || false}
                onCheckedChange={(checked: boolean) => handleCheckboxChange('RequireCustomerTcNo', checked)}
              />
            </div>

            <div className="form-group checkbox-group">
              <IPCheckBox
                label="Müşteri Adı Gerekli"
                checked={state.form.RequireCustomerName || false}
                onCheckedChange={(checked: boolean) => handleCheckboxChange('RequireCustomerName', checked)}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <IPButton
            onClick={handleSave}
            disabled={state.isSaving}
            text={state.isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            className="btn-primary"
          />
          <IPButton
            onClick={handleReset}
            disabled={state.isSaving}
            text="Sıfırla"
            className="btn-secondary"
          />
        </div>
      </form>

      {/* Kategori Ayarları Formu */}
      <div className="category-settings-section">
        <div className="settings-header">
          <h2>Kategori Ayarları</h2>
          <IPButton
            onClick={handleAddCategory}
            disabled={state.isSavingCategories}
            text="+ Yeni Kategori Ekle"
            className="btn-primary"
          />
        </div>

        {state.isLoadingCategories ? (
          <div className="shopping-credit-settings loading">Kategori ayarları yükleniyor...</div>
        ) : (
          <form className="settings-form">
            {state.categoryForm.length > 0 ? (
              <>
                <div className="category-table-wrapper">
                  <table className="category-table">
                    <thead>
                      <tr>
                        <th>Kategori Adı</th>
                        <th>Aktif</th>
                        <th>Minimum Fiyat (₺)</th>
                        <th>Maksimum Fiyat (₺)</th>
                        <th>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.categoryForm.map((category, index) => (
                        <tr key={`${category.Id}-${index}`}>
                          <td>
                            <IPInput
                              type="text"
                              value={category.CategoryName || ''}
                              onChange={(e: any) =>
                                handleCategorySettingChange(index, 'CategoryName', e.target.value)
                              }
                              placeholder="Kategori adını girin"
                            />
                          </td>
                          <td>
                            <IPCheckBox
                              checked={category.IsActive || false}
                              onCheckedChange={(checked: boolean) =>
                                handleCategorySettingChange(index, 'IsActive', checked)
                              }
                            />
                          </td>
                          <td>
                            <IPInput
                              type="number"
                              value={category.MinPrice?.toString() || ''}
                              onChange={(e: any) =>
                                handleCategorySettingChange(index, 'MinPrice', parseFloat(e.target.value) || undefined)
                              }
                              placeholder="Min fiyat (isteğe bağlı)"
                            />
                          </td>
                          <td>
                            <IPInput
                              type="number"
                              value={category.MaxPrice?.toString() || ''}
                              onChange={(e: any) =>
                                handleCategorySettingChange(index, 'MaxPrice', parseFloat(e.target.value) || undefined)
                              }
                              placeholder="Maks fiyat (isteğe bağlı)"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(index)}
                              className="delete-btn"
                              title="Kategoriyi Sil"
                            >
                              <i className="icon-trash-2"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="form-actions">
                  <IPButton
                    onClick={handleCategorySave}
                    disabled={state.isSavingCategories}
                    text={state.isSavingCategories ? 'Kaydediliyor...' : 'Kategori Ayarlarını Kaydet'}
                    className="btn-primary"
                  />
                  <IPButton
                    onClick={handleCategoryReset}
                    disabled={state.isSavingCategories}
                    text="Sıfırla"
                    className="btn-secondary"
                  />
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>Henüz hiç kategori bulunamadı. Yeni kategori ekleyerek başlayın.</p>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  )
}

export default componentWithContext(ShoppingCreditSettings)
